import {etsySettings} from '@coolcolduk/etsy';
import {uniqueArray} from '@coolcolduk/util';
import CheckIcon from '@mui/icons-material/Check';
import StopIcon from '@mui/icons-material/Stop';
import {Box, Chip, Grid, TextField, Typography} from '@mui/material';
import {Dispatch, SetStateAction} from 'react';

export interface EtsyUpdateListingSeoItemEditorTagsProps {
  tags: string[];
  selectedTags: string[];
  setTags: Dispatch<SetStateAction<string[]>>;
  setSelectedTags: Dispatch<SetStateAction<string[]>>;
}

export default function EtsyUpdateListingSeoItemEditorTags({
  tags,
  selectedTags,
  setTags,
  setSelectedTags,
}: EtsyUpdateListingSeoItemEditorTagsProps) {
  const handleDeleteTag = (tag: string) => {
    setTags(tags.filter((t) => t !== tag));
    setSelectedTags(selectedTags.filter((t) => t !== tag));
  };

  const handleTagClick = (tag: string) => {
    setSelectedTags((prev) => (prev.includes(tag) ? prev.filter((t) => t !== tag) : [...prev, tag]));
  };

  const handleAddTag = (e: React.KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && (e.target as HTMLInputElement).value) {
      const target = e.target as HTMLInputElement;
      const allNew = target.value
        .split(',')
        .map((t) => t.trim())
        .filter(Boolean);

      const valid = allNew.filter((t) => t.length <= etsySettings.listing.tags.maxLength);
      const invalid = allNew.filter((t) => t.length > etsySettings.listing.tags.maxLength);
      setTags(uniqueArray([...tags, ...valid]));

      if (selectedTags.length < etsySettings.listing.tags.maxLength) {
        setSelectedTags(uniqueArray([...selectedTags, ...valid]).slice(0, etsySettings.listing.tags.maxCount));
      }

      target.value = invalid.join(',');
    }
  };

  const isMoreThanMaxTag = selectedTags.length > etsySettings.listing.tags.maxCount;

  return (
    <Grid>
      <Box sx={{mb: 1}}>
        <Typography
          sx={{
            pl: 2,
            backgroundColor: isMoreThanMaxTag ? 'warning.main' : undefined,
            color: isMoreThanMaxTag ? 'primary.contrastText' : undefined,
          }}
        >
          Tags ({selectedTags.length}/{etsySettings.listing.tags.maxCount}) /total: {tags.length}
        </Typography>
      </Box>
      <Grid container direction={{xs: 'column', sm: 'row'}}>
        <TextField
          fullWidth
          size="small"
          placeholder="Add tag and press Enter"
          onKeyDown={handleAddTag}
          helperText={`tags will not be added if they are longer than ${etsySettings.listing.tags.maxLength} characters`}
          sx={{mt: 1}}
        />
        {tags.map((tag) => (
          <Grid key={tag}>
            <Chip
              label={tag}
              size="small"
              onDelete={() => handleDeleteTag(tag)}
              onClick={() => handleTagClick(tag)}
              icon={selectedTags.includes(tag) ? <CheckIcon /> : <StopIcon />}
              sx={{
                m: 0.5,
                bgcolor: selectedTags.includes(tag) ? 'success.light' : 'default',
              }}
            />
          </Grid>
        ))}
      </Grid>
    </Grid>
  );
}
